import type { UIViewController } from "uicore-ts"


/**
 * Implemented by content view controllers that wish to be asked before route
 * navigation resets or replaces their current state.
 *
 * RootViewController calls `navigationWillReset()` from the global UIRoute
 * pre-navigation hook when a route change may remove, force-reset, reparameterize,
 * or mode-switch the current content view controller.  The VC may return `false`
 * to cancel the navigation — for example, when there is unsaved data that would
 * be lost.
 *
 * ### Minimal implementation
 *
 * ```typescript
 * class MyViewController extends UIViewController implements RHNavigationResettable {
 *
 *     async navigationWillReset(): Promise<boolean> {
 *         if (this._hasUnsavedChanges) {
 *             const confirmed = await CBDialogViewShower.showConfirmDialog(
 *                 "Salvestamata muudatused lähevad kaotsi. Jätka?"
 *             )
 *             return confirmed
 *         }
 *         return true
 *     }
 *
 * }
 * ```
 *
 * A VC that needs no guard at all still benefits from implementing the
 * interface with a trivial `return true` body, because it documents intent.
 * VCs that do NOT implement the interface are always reset without prompting.
 */
export interface RHNavigationResettable {

    /**
     * Called before guarded route navigation resets this view controller.
     *
     * Return `true` to allow the reset, `false` to cancel it.
     * Returning `false` leaves the current view and route untouched.
     *
     * This method may be async — e.g. to show a confirmation dialog and wait
     * for the user's answer.
     */
    navigationWillReset(): Promise<boolean>

}

/**
 * Type guard.  Returns `true` when `value` implements `RHNavigationResettable`.
 */
export function isRHNavigationResettable(value: UIViewController | undefined): value is UIViewController & RHNavigationResettable {
    return value !== undefined && "navigationWillReset" in value && typeof value.navigationWillReset === "function"
}
